import { Injectable, signal } from '@angular/core';

/**
 * Toolbar / chrome de Safari suele restar ~40–80px al visualViewport.
 * Por debajo de este umbral no tratamos el hueco como teclado (evita esconder
 * o desalinear el dock móvil).
 */
const KEYBOARD_INSET_MIN_PX = 120;

/** Desviación de scale respecto a 1 → pinch-zoom; no es teclado. */
const PINCH_ZOOM_SCALE_EPSILON = 0.02;

/**
 * Calcula el inset inferior atribuible a teclado virtual.
 * Exportado para tests; ignora pinch-zoom y huecos pequeños (toolbar).
 */
export function computeAdminKeyboardInsetPx(
  innerHeight: number,
  innerWidth: number,
  viewport: Pick<VisualViewport, 'height' | 'width' | 'offsetTop'>
): number {
  const layoutWidth = innerWidth > 0 ? innerWidth : 1;
  const scale = viewport.width / layoutWidth;
  if (Math.abs(scale - 1) > PINCH_ZOOM_SCALE_EPSILON) {
    return 0;
  }
  const raw = Math.max(0, innerHeight - viewport.height - viewport.offsetTop);
  return raw >= KEYBOARD_INSET_MIN_PX ? Math.round(raw) : 0;
}

/**
 * Inset inferior cuando el teclado virtual reduce visualViewport (iOS Safari / Android).
 * Fallback seguro si no existe visualViewport (inset 0).
 * Expone `--milinco-kb-inset` en documentElement para chrome admin (dock / save bar).
 */
@Injectable({ providedIn: 'root' })
export class AdminKeyboardInsetService {
  private listeners = 0;

  readonly bottomInsetPx = signal(0);

  attach(): () => void {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return () => undefined;
    }

    this.listeners += 1;
    if (this.listeners === 1) {
      window.visualViewport.addEventListener('resize', this.sync);
      window.visualViewport.addEventListener('scroll', this.sync);
      this.sync();
    }

    return () => {
      this.listeners = Math.max(0, this.listeners - 1);
      if (this.listeners === 0) {
        window.visualViewport?.removeEventListener('resize', this.sync);
        window.visualViewport?.removeEventListener('scroll', this.sync);
        this.bottomInsetPx.set(0);
        document.documentElement.style.removeProperty('--milinco-kb-inset');
      }
    };
  }

  private readonly sync = (): void => {
    const viewport = window.visualViewport;
    if (!viewport) {
      return;
    }
    const px = computeAdminKeyboardInsetPx(window.innerHeight, window.innerWidth, viewport);
    this.bottomInsetPx.set(px);
    if (px > 0) {
      document.documentElement.style.setProperty('--milinco-kb-inset', `${px}px`);
    } else {
      document.documentElement.style.removeProperty('--milinco-kb-inset');
    }
  };
}
